"use client";

import type React from "react";
import { Box, Text } from "@chakra-ui/react";
import { Bot } from "lucide-react";
import type { Node } from "./types";
import { useAppTheme } from "../../../ThemeContext.tsx";

interface FlowNodeProps {
  node: Node;
  selected: boolean;
  onDragStart: (e: React.MouseEvent) => void;
  onDrag: (e: React.MouseEvent) => void;
  onDragEnd: () => void;
  onConnectorMouseDown: (
    nodeId: string,
    point: "top" | "bottom",
    handleIndex: number,
    e: React.MouseEvent
  ) => void;
}

export const FlowNode: React.FC<FlowNodeProps> = ({
  node,
  selected,
  onDragStart,
  onDrag,
  onDragEnd,
  onConnectorMouseDown,
}) => {
  const isStart = node.type === "start";
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  return (
    <Box
      position="relative"
      width="270px"
      data-node-id={node.id}
      onMouseDown={(e) => onDragStart(e)}
      onMouseMove={(e) => onDrag(e)}
      onMouseUp={onDragEnd}
      cursor="move"
    >
      {/* Starting State Label */}
      {isStart && (
        <Text
          position="absolute"
          top="-25px"
          left="0"
          width="100%"
          textAlign="center"
          fontSize="sm"
          pointerEvents="none"
          color={isDark ? "gray.400" : "gray.600"}
        >
          Starting State
        </Text>
      )}

      {/* Node Content */}
      <Box
        bg="gray.800"
        border="1px solid"
        borderColor={selected ? "gray.500" : "gray.700"}
        borderRadius="md"
        p={4}
        _hover={{ borderColor: "gray.400" }}
        backgroundColor={isDark ? "gray.700" : "white"}
      >
        <Box mb={2} display="flex" alignItems="center" gap={2}>
          <Bot size={20} color="#48BB78" />
          {/* Display the state_name instead of name */}
          <Text
            color={isDark ? "gray.400" : "gray.600"}
            fontSize="sm"
            fontWeight="medium"
          >
            {node.data?.state_name || node.id}
          </Text>
        </Box>
        {/* State Prompt Preview */}
        {node.data?.state_prompt && (
          <Text
            color={isDark ? "gray.400" : "gray.600"}
            fontSize="xs"
            noOfLines={2}
          >
            {node.data.state_prompt}
          </Text>
        )}
      </Box>

      {/* Connectors */}
      <Box
        position="absolute"
        top="-15px" // Moved higher up to increase the clickable area
        left="50%"
        transform="translateX(-50%)"
        width="30px" // Increased width for larger hit area
        height="30px" // Increased height for larger hit area
        cursor="pointer"
        data-connector="top"
        data-handle-index="0"
        onMouseDown={(e) => onConnectorMouseDown(node.id, "top", 0, e)}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {/* Visual connector dot remains the same size */}
        <Box
          width="10px"
          height="10px"
          borderRadius="full"
          bg="gray.600"
          _hover={{ bg: "gray.400" }}
        />
      </Box>

      <Box
        position="absolute"
        bottom="-15px" // Moved lower to increase the clickable area
        left="50%"
        transform="translateX(-50%)"
        width="30px" // Increased width for larger hit area
        height="30px" // Increased height for larger hit area
        cursor="pointer"
        data-connector="bottom"
        data-handle-index="0"
        onMouseDown={(e) => onConnectorMouseDown(node.id, "bottom", 0, e)}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {/* Visual connector dot remains the same size */}
        <Box
          width="10px"
          height="10px"
          borderRadius="full"
          bg="gray.600"
          _hover={{ bg: "gray.400" }}
        />
      </Box>
    </Box>
  );
};
